import styles from './index.module.less'
import { Col, Row, Input, Button, Avatar, Spin } from 'antd'
import AutoSizer from 'react-virtualized-auto-sizer'
import MessageItem from '@/pages/messageChat/MessageItem'
import { useDebounceEffect, useSetState } from 'ahooks'

import { Scrollbars } from 'react-custom-scrollbars'
import Icon, {
  SendOutlined,
  LoadingOutlined,
  UserOutlined,
  RedoOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons'
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'
import { isNil } from 'lodash-es'
import { useActivate } from 'react-activation'
import classNames from 'classnames'
import { getImageUrl } from '@/utils/image.ts'

const ChatDialog = forwardRef((props: any, ref) => {
  const {
    data,
    messageData,
    urlPrefix,
    onReachTop,
    onReachBottom,
    onScrollFrame,
    onScroll,
    onReload,
    defaultScrollTop,
    loading,
    noMore,
    hideInput,
    onMessageItemClick,
    lastUpdatedTime,
    deps = [],
  } = props

  const scrollRef = useRef<any>()
  const timerRef = useRef<any>()
  const isScroll = useRef(false)

  const [state, setState]: any = useSetState({
    loading: false,
    jumpLatestShow: false,
  })

  const handleScroll = values => {
    // console.log('values', values, defaultScrollTop)
    isScroll.current = true
    timerRef.current = setTimeout(() => {
      if (values.scrollTop <= 0) {
        onReachTop?.()
      }

      if (values.top === 1) {
        onReachBottom?.()
        reload()
      }

      setState({
        jumpLatestShow: values.top < 0.8,
      })
      onScrollFrame?.(values)
      onScroll?.(values)
    }, 100)
  }

  const handleMouse = e => {
    console.log(e)
  }

  const handleScrollStop = () => {
    isScroll.current = false
  }

  const scrollTopFix = () => {
    clearInterval(timerRef.current)
    if (!isNil(defaultScrollTop)) {
      scrollRef?.current?.scrollTop?.(defaultScrollTop)
    } else {
      scrollRef?.current?.scrollToBottom?.()
    }
  }

  useEffect(() => {
    if (data) {
      scrollTopFix()
      requestAnimationFrame(scrollTopFix)
    }
  }, [data, ...deps])

  const scrollToBottom = () => {
    scrollRef?.current?.scrollToBottom?.()
  }

  const scrollTop = data => {
    scrollRef?.current?.scrollTop?.(data.detail)
  }

  const pullDataScrollTo = data => {
    if (!isScroll.current) {
      scrollRef?.current?.scrollTop?.(data.detail)
    }
  }

  const reload = async () => {
    setState({ loading: true })
    try {
      await onReload?.(data)
    } finally {
      setState({ loading: false })
    }
  }

  useDebounceEffect(
    () => {
      window.addEventListener('SCROLL_TO_BOTTOM', scrollToBottom)
      window.addEventListener('SCROLL_TO', scrollTop)
      window.addEventListener('SCROLL_TO_FIX', pullDataScrollTo)
      return () => {
        window.removeEventListener('FETCH_MESSAGE_LIST', scrollToBottom)
        window.removeEventListener('SCROLL_TO', scrollTop)
        window.removeEventListener('SCROLL_TO_FIX', pullDataScrollTo)
      }
    },
    [],
    { wait: 200 }
  )

  useActivate(() => {
    setTimeout(() => {
      scrollTopFix()
    }, 20)
  })

  useImperativeHandle(ref, () => ({
    scrollToBottom: scrollRef?.current?.scrollToBottom,
    scrollTop: scrollRef?.current?.scrollTop,
  }))
  return (
    <div className={classNames(styles.chatDialog, 'chat-dialog-wrap')}>
      <Row gutter={12} align="middle" className={styles.chatHeader}>
        {data ? (
          <>
            <Col>
              {/*<div className={styles.chatAvatar}></div>*/}
              <Avatar
                src={getImageUrl(data.avatarUrl, urlPrefix)}
                size={30}
                icon={<UserOutlined />}
              />
            </Col>
            <Col>
              <div className={styles.chatName}>{data?.name}</div>
            </Col>
            <Col>
              <div className={styles.chatDesc}>{data?.desc}</div>
            </Col>
          </>
        ) : null}
      </Row>

      {data ? (
        <AutoSizer>
          {({ height, width }) => {
            // Use these actual sizes to calculate your percentage based sizes
            return (
              <Scrollbars
                ref={scrollRef}
                autoHide
                renderTrackHorizontal={() => <div></div>}
                style={{
                  width,
                  height: height - 57.14 - (hideInput ? 0 : 80),
                  paddingBottom: hideInput ? 20 : 60,
                }}
                onScrollFrame={handleScroll}
                onScrollStop={handleScrollStop}
                onMouseWheel={handleMouse}
              >
                <div className={styles.scroller}>
                  {messageData?.map?.((d, i) => (
                    <MessageItem
                      key={`message_item_${i}`}
                      data={d}
                      avatar={getImageUrl(data.avatarUrl, urlPrefix)}
                      onClick={onMessageItemClick}
                    />
                  ))}
                  <div className={styles.scrollerState}>
                    {loading ? (
                      <div className={styles.scrollerLoading}>
                        <Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} />
                        <span className={styles.scrollerStateText}>加载中</span>
                      </div>
                    ) : null}
                    {!loading && noMore ? (
                      <div className={styles.scrollNoMore}>
                        <Icon
                          style={{ fontSize: 16 }}
                          component={() => (
                            <svg
                              className="icon"
                              viewBox="0 0 1024 1024"
                              version="1.1"
                              xmlns="http://www.w3.org/2000/svg"
                              width="1em"
                              height="1em"
                            >
                              <path d="M509.7 609.7c140.9 0 273.8 67.4 364.5 185 14.3 18.5 12.4 46-4.4 62.5-6.9 6.7-15.9 10.5-25.5 10.5-12 0-23.4-5.6-31.3-15.4-76.7-98.3-187.7-154.7-304.4-154.7-117.5 0-228.5 56.4-304.5 154.8-7.7 9.9-18.6 15.6-30.1 15.6-9.4 0-18.5-3.8-25.6-10.8-8.5-7.9-13.5-18.6-14.2-30.3-0.7-11.4 2.8-22.4 9.8-31 92.5-118.4 225.7-186.2 365.7-186.2z m228.5-189.4c-36.6 0-67.5-34.8-67.5-76v-111c0-41.2 30.9-76 67.5-76s67.5 34.8 67.5 76v111c0 41.9-30.3 76-67.5 76z m-458.2 0c-36.6 0-67.5-34.8-67.5-76v-111c0-41.2 30.9-76 67.5-76s67.5 34.8 67.5 76v111c0 41.1-30.9 76-67.5 76z m0 0"></path>
                            </svg>
                          )}
                        />
                        <span className={styles.scrollerStateText}>没有更多了</span>
                      </div>
                    ) : null}
                  </div>
                </div>
                {lastUpdatedTime ? (
                  <Row justify="center" align="middle">
                    <Col style={{ width: 190, textAlign: 'center', color: '#666' }}>
                      更新于{lastUpdatedTime}
                    </Col>
                    <Col>
                      <Button
                        type="text"
                        icon={<RedoOutlined spin={state.loading} />}
                        size="small"
                        shape="circle"
                        onClick={reload}
                      />
                    </Col>
                  </Row>
                ) : null}
              </Scrollbars>
            )
          }}
        </AutoSizer>
      ) : null}

      <div className={classNames(styles.jumpLatest, { [styles.visible]: state.jumpLatestShow })}>
        <Button type="primary" ghost shape="round" onClick={scrollToBottom} icon={<ArrowDownOutlined />}>
          回到最新位置
        </Button>
      </div>

      {hideInput ? null : (
        <div className={styles.dialogBottom}>
          <Row justify="end">
            <Row justify="end" align="middle" className={styles.dialogActions}>
              {[
                {
                  label: '报价',
                },
                {
                  label: '跟进',
                },
                {
                  label: '提单',
                },
              ].map((d, i) => (
                <Col key={`action_${i}`} className={styles.actionItem}>
                  <Button type="text" shape="round">
                    {d.label}
                  </Button>
                </Col>
              ))}
            </Row>
          </Row>

          <div>
            <Input.TextArea
              autoSize={false}
              rows={1}
              size="large"
              style={{ resize: 'none', borderRadius: 5 }}
              placeholder={`发送给  ${data?.name || ''}`}
              // addonAfter={<div>123</div>}
            />
            <Button className={styles.sendBtn} type="text" shape="circle">
              <SendOutlined />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
})

export default ChatDialog
